// ContactUs.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaFacebookF, FaInstagram, FaWhatsapp } from 'react-icons/fa';
import { FaTiktok } from 'react-icons/fa6';
import contactUsBg from '../assets/Contact/pexels-chanthusa-wijayawickrama-362149114-14372006.jpg';

// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const ContactUs = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        const phoneNumber = '94770806767'; // Replace with your WhatsApp number
        const text = `Pick Lanka Tour Inquiry
Name: ${formData.name}
Email: ${formData.email}
Contact Number: ${formData.contactNumber}
Message: ${formData.message}`;
        const formattedMessage = encodeURIComponent(text);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${formattedMessage}`;

        try {
            window.open(whatsappUrl, '_blank');
            setTimeout(() => {
                setIsSubmitting(false);
                setSuccessMessage(t('successMessage'));
                setFormData({ name: '', email: '', contactNumber: '', message: '' });
            }, 1000);
        } catch (error) {
            setIsSubmitting(false);
            setErrorMessage(t('errorMessage'));
        }
    };

    return (
        <div className="bg-black">
            {/* ===== HERO SECTION ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[50vh] lg:h-[80vh]"
            >
                <img
                    src={contactUsBg}
                    alt={t('contactUsTitle')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-[100px]">
                    <div className="w-full">
                        <h1 className="text-center text-[50px] font-semibold leading-[80px] text-white font-copperplate md:text-[60px] lg:text-[80px]">
                            {t('contactUsTitle')}
                        </h1>
                        <div className="flex justify-center text-center leading-8">
                            <p className="mt-6 max-w-[90%] font-lato text-[16px] font-semibold tracking-[5%] text-[#96BA33] sm:mt-10 sm:max-w-2xl sm:text-[25px]">
                                {t('contactUsSubtitle')}
                            </p>
                        </div>

                        <div className="absolute left-[30px] top-10">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-10 right-[30px]">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ===== CONTACT INFO + FORM (BLACK BG) ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-12"
            >
                <div className="container mx-auto px-4">
                    <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
                        {/* Left: Contact Info */}
                        <div className="w-full lg:w-1/2 space-y-8">
                            <div>
                                <h2 className="text-[#009CE6] text-xl md:text-2xl font-light tracking-wider font-inknut">
                                    {t('feelFreeToContact')}
                                </h2>
                                <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mt-2 font-konkhmer">
                                    {t('planYourDream')}
                                </h1>
                                <p className="text-white/80 text-sm md:text-base lg:text-lg leading-relaxed mt-4 font-inknut">
                                    {t('contactDescription')}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                                {/* Call Center */}
                                <div>
                                    <h3 className="text-[#009CE6] text-lg md:text-xl font-semibold tracking-wider font-konkhmer">
                                        {t('callCenter')}
                                    </h3>
                                    <p className="text-white text-base md:text-lg font-medium mt-2">+94 11 273 1374</p>
                                    <p className="text-white text-base md:text-lg font-medium">+94 77 080 6767</p>
                                </div>

                                {/* Email */}
                                <div>
                                    <h3 className="text-[#009CE6] text-lg md:text-xl font-semibold tracking-wider font-konkhmer">
                                        {t('email')}
                                    </h3>
                                    <p className="text-white text-base md:text-lg font-medium mt-2">info@picklanka.com</p>
                                </div>

                                {/* Location */}
                                <div>
                                    <h3 className="text-[#009CE6] text-lg md:text-xl font-semibold tracking-wider font-konkhmer">
                                        {t('ourLocation')}
                                    </h3>
                                    <p className="text-white text-base md:text-lg font-medium leading-relaxed mt-2">
                                        164, Jambugasmulla Mawatha, Nugegoda, Sri Lanka.
                                    </p>
                                </div>

                                {/* Social Network */}
                                <div>
                                    <h3 className="text-[#009CE6] text-lg md:text-xl font-semibold tracking-wider font-konkhmer">
                                        {t('socialNetwork')}
                                    </h3>
                                    <div className="flex space-x-4 mt-3">
                                        <a href="https://www.facebook.com/TravelwithPicklanka?mibextid=LQQJ4d" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                            <FaFacebookF className="h-7 w-7 text-[#009CE6] hover:text-[#96BA33] transition-colors" />
                                        </a>
                                        <a href="https://www.instagram.com/travelwithpicklanka?igsh=cmNwamJ5NW9teHk4&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                            <FaInstagram className="h-7 w-7 text-[#009CE6] hover:text-[#96BA33] transition-colors" />
                                        </a>
                                        <a href="https://wa.me/+94770806767" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                                            <FaWhatsapp className="h-7 w-7 text-[#009CE6] hover:text-[#96BA33] transition-colors" />
                                        </a>
                                        <a href="https://www.tiktok.com/@picklanka" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                            <FaTiktok className="h-7 w-7 text-[#009CE6] hover:text-[#96BA33] transition-colors" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Contact Form */}
                        <div className="w-full lg:w-1/2">
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                className="bg-white rounded-2xl p-6 md:p-10 shadow-2xl"
                            >
                                <h2 className="text-[#009CE6] text-2xl md:text-3xl font-semibold tracking-wider font-konkhmer">
                                    {t('getInTouch')}
                                </h2>
                                <p className="text-gray-700 text-sm md:text-base leading-relaxed mt-2 font-inknut">
                                    {t('getInTouchDesc')}
                                </p>

                                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                                    <div>
                                        <label className="block text-[#009CE6] text-sm md:text-base font-semibold tracking-wider">
                                            {t('name')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full mt-1 border-b-2 border-[#009CE6] bg-transparent py-2 text-gray-800 text-base outline-none focus:border-[#96BA33] transition"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[#009CE6] text-sm md:text-base font-semibold tracking-wider">
                                            {t('emailLabel')}
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full mt-1 border-b-2 border-[#009CE6] bg-transparent py-2 text-gray-800 text-base outline-none focus:border-[#96BA33] transition"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[#009CE6] text-sm md:text-base font-semibold tracking-wider">
                                            {t('contactNumber')}
                                        </label>
                                        <input
                                            type="tel"
                                            name="contactNumber"
                                            value={formData.contactNumber}
                                            onChange={handleChange}
                                            className="w-full mt-1 border-b-2 border-[#009CE6] bg-transparent py-2 text-gray-800 text-base outline-none focus:border-[#96BA33] transition"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[#009CE6] text-sm md:text-base font-semibold tracking-wider">
                                            {t('message')}
                                        </label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full mt-1 border-b-2 border-[#009CE6] bg-transparent py-2 text-gray-800 text-base outline-none focus:border-[#96BA33] transition resize-none"
                                            rows={4}
                                            required
                                        />
                                    </div>

                                    {successMessage && (
                                        <p className="text-[#96BA33] text-sm md:text-base font-medium">{successMessage}</p>
                                    )}
                                    {errorMessage && (
                                        <p className="text-red-600 text-sm md:text-base font-medium">{errorMessage}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`mt-6 flex h-12 w-full md:w-auto items-center justify-center space-x-2 rounded-full bg-[#009CE6] px-8 font-semibold text-white text-base md:text-lg transition-all hover:bg-[#96BA33] ${isSubmitting ? 'cursor-not-allowed opacity-70' : ''}`}
                                    >
                                        <span>{isSubmitting ? t('sending') : t('sendMessage')}</span>
                                        {!isSubmitting && (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" />
                                            </svg>
                                        )}
                                    </button>
                                </form>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== LOCATION MAP SECTION (BLACK BG) ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-12"
            >
                <div className="container mx-auto px-4">
                    <h2
                        className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-16"
                        style={{
                            background: "linear-gradient(to right, #009CE6, #96BA33)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}
                    >
                        {t('findUsInColombo')}
                    </h2>
                    <div className="flex justify-center">
                        <div className="w-full">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5116.585708821106!2d79.8946627!3d6.8632995999999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae25b421c8a86ab%3A0xa816edf0085a06ae!2sPick%20Lanka!5e1!3m2!1sen!2slk!4v1762062289334!5m2!1sen!2slk"
                                width="100%"
                                height="500"
                                className="rounded-2xl shadow-2xl border-0"
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title={t('findUsInColombo')}
                            ></iframe>
                        </div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default ContactUs;